/**
 * CampaignHierarchy — edits the tree of SFDC campaign ids an event carries.
 *
 * The Figma draws two fixed fields, `Child Campaign ID` and `Grandchild Campaign ID`, but
 * the requirement is a whole tree at arbitrary depth, so this is a tree editor: every node
 * can take a child, and nesting goes as deep as the marketer needs. The design's "child" is
 * depth 1 and "grandchild" is depth 2; nothing stops there.
 *
 * Rendered by hand rather than with Cloudscape's TreeView, which is present in the package
 * but not usable at this version: `exports` does not expose `./polaris/tree-view` and the
 * main entry does not export it either (only its test-utils wrapper ships). Swap this for
 * TreeView once the component is actually published — the shape here deliberately matches
 * what it would need.
 *
 * The event itself is the root and is not editable: the hierarchy hangs off this event, so
 * a field for it would invite pointing it at some other campaign. It reads "CampaignId (Event
 * Name)" — the event's own SFDC Campaign ID is the parent of everything below. What the marketer
 * adds are its descendants.
 *
 * A row is one of two kinds, and every row says which with a tag at its start:
 *   Campaign — an SFDC campaign id typed in (an EXTERNAL_CAMPAIGN node);
 *   Event    — another Event Hub event chosen from a list labelled "Event Name - CampaignId"
 *              (a LIFE_CYCLE_EVENT node). Its campaign id is that event's own, never typed here.
 * The kind is chosen once, when the row is added: the root and every campaign row carry an
 * "Add child" menu offering both, so an event can go at any depth. The list only offers events
 * that back no hierarchy node yet, and offers events with no SFDC Campaign ID disabled, saying
 * why (eventOptions in campaignHierarchy.ts). Once saved, an event row is read-only text.
 *
 * An event row takes children like any other row. The hierarchy is one tree in the service, so
 * what is added under an event here also shows in that event's own hierarchy — the same nodes,
 * seen from its node. Removing an event row is blocked only while it has SAVED children (see
 * hierarchySync): the service refuses to delete a node with children, and deleting them for the
 * marketer would wipe campaigns that may have been put there from that other event.
 *
 * Controlled — the form owns the tree, so Cancel discards it along with everything else.
 * Every mutation goes through the pure helpers in campaignHierarchy.ts, which are tested
 * without rendering.
 */

import { useState } from 'react';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import Badge from '@amzn/awsui-components-react/polaris/badge';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import ButtonDropdown, {
  type ButtonDropdownProps,
} from '@amzn/awsui-components-react/polaris/button-dropdown';
import Input from '@amzn/awsui-components-react/polaris/input';
import Select, { type SelectProps } from '@amzn/awsui-components-react/polaris/select';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import {
  addChild,
  countNodes,
  duplicateCampaignIds,
  hasSavedChildren,
  eventIdsInTree,
  eventOptions,
  isEventRow,
  newNodeOfKind,
  removeNode,
  rootLabel,
  setCampaignId,
  setEvent,
  type EventOption,
  type HierarchyNode,
  type NodeKind,
  type PickableEvent,
} from './campaignHierarchy';
import './CampaignHierarchy.css';

export type EventPickerSource =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; events: readonly PickableEvent[]; withNodes: ReadonlySet<string> };

/** What every row needs to know about the events it can choose from. Same for the whole tree. */
interface EventChoices {
  /** Absent: the editor was given no event list, so "Event Hub event" is offered disabled. */
  picker?: EventPickerSource;
  /** Choosable events, already excluding this event, events in the tree, and placed events. */
  options: readonly EventOption[];
  /** "Event Name - CampaignId" (or the name alone) by event id, for chosen and saved rows. */
  labels: ReadonlyMap<string, string>;
}

/**
 * The "Add child" menu: the one place a row's kind is decided, so the two kinds are offered side
 * by side with a line each on what they mean, rather than as two buttons that look alike.
 */
function AddChildMenu({
  under,
  canAddEvent,
  onAdd,
}: {
  /** What the child goes under, for the accessible name. */
  under: string;
  canAddEvent: boolean;
  onAdd: (kind: NodeKind) => void;
}) {
  const items: ButtonDropdownProps.Item[] = [
    {
      id: 'campaign',
      text: 'SFDC Campaign ID',
      secondaryText: 'Type in a campaign ID',
    },
    {
      id: 'event',
      text: 'Event Hub event',
      secondaryText: 'Choose another event; its campaign ID is used',
      disabled: !canAddEvent,
      disabledReason: 'The event list is not available',
    },
  ];
  return (
    <ButtonDropdown
      items={items}
      ariaLabel={`Add a child under ${under}`}
      onItemClick={({ detail }) => onAdd(detail.id as NodeKind)}
    >
      Add child
    </ButtonDropdown>
  );
}

interface RowProps {
  node: HierarchyNode;
  /** What this row hangs from — the event at depth 1, otherwise the parent's label. */
  parentLabel: string;
  /** Position among its siblings, for a name that identifies which row this is. */
  index: number;
  siblingCount: number;
  /** Campaign ids used more than once anywhere in the tree, so a row can mark itself. */
  duplicates: ReadonlySet<string>;
  events: EventChoices;
  collapsed: ReadonlySet<string>;
  onToggle: (key: string) => void;
  onEdit: (key: string, sfdcCampaignId: string) => void;
  onChooseEvent: (key: string, eventId: string) => void;
  onAddChild: (key: string, kind: NodeKind) => void;
  onRemove: (key: string) => void;
}

/**
 * One node and its subtree.
 *
 * Recursive, and an `<ul>`/`<li>` at every level rather than a flat list with padding: the
 * nesting is the meaning here, so it belongs in the markup where a screen reader can
 * follow it, not only in the indentation.
 */
function Row({
  node,
  parentLabel,
  index,
  siblingCount,
  duplicates,
  events,
  collapsed,
  onToggle,
  onEdit,
  onChooseEvent,
  onAddChild,
  onRemove,
}: RowProps) {
  const id = node.sfdcCampaignId.trim();
  const duplicate = id !== '' && duplicates.has(id);
  const isEvent = isEventRow(node);
  const eventLabel = node.eventId
    ? events.labels.get(node.eventId) ?? (id ? `${node.eventId} - ${id}` : node.eventId)
    : undefined;
  const label = isEvent ? eventLabel || 'this event row' : id || 'this campaign';
  const descendants = countNodes(node.children);
  const isCollapsed = collapsed.has(node.key);

  /* Every field on the page needs a name that says which one it is. Indentation and the
     connector lines answer that visually, but neither reaches a screen reader, and "Campaign
     ID" repeated down the tree is indistinguishable — so the position and the parent go into
     the name. The nested <ul>/<li> markup conveys the depth on top of this. */
  const position = `${index + 1} of ${siblingCount} under ${parentLabel}`;

  let control: React.ReactNode;
  if (!isEvent) {
    control = (
      <div className="eh-hierarchy__input">
        <Input
          value={node.sfdcCampaignId}
          placeholder="Type an SFDC Campaign ID"
          /* Not "SFDC Campaign ID" — the event has its own field by that name in Basic
             Information, and a duplicate accessible name is ambiguous. */
          ariaLabel={`Campaign ID ${position}`}
          invalid={duplicate}
          onChange={({ detail }) => onEdit(node.key, detail.value)}
        />
      </div>
    );
  } else if (node.nodeId) {
    // Saved: which event this is cannot change (an event backs one node), so it is plain text.
    control = (
      <div className="eh-hierarchy__event">
        <Box variant="span">{eventLabel}</Box>
      </div>
    );
  } else {
    const picker = events.picker;
    const selected: SelectProps.Option | null = node.eventId
      ? { value: node.eventId, label: eventLabel }
      : null;
    control = (
      <div className="eh-hierarchy__event">
        <Select
          selectedOption={selected}
          onChange={({ detail }) => {
            const v = detail.selectedOption.value;
            if (v) onChooseEvent(node.key, v);
          }}
          options={events.options as SelectProps.Options}
          placeholder="Choose an event"
          ariaLabel={`Event ${position}`}
          filteringType="auto"
          invalid={duplicate}
          statusType={
            picker?.status === 'loading' ? 'loading' : picker?.status === 'error' ? 'error' : 'finished'
          }
          loadingText="Loading events"
          errorText={picker?.status === 'error' ? `Could not load events: ${picker.message}` : undefined}
          empty="No events available — every other event is already in a hierarchy"
        />
      </div>
    );
  }

  return (
    <li className="eh-hierarchy__item">
      <div className="eh-hierarchy__row">
        {/* A fixed-width slot whether or not there is a toggle, so rows at the same depth line
            up even when only some rows have children. */}
        <span className="eh-hierarchy__toggle">
          {node.children.length > 0 ? (
            <Button
              variant="inline-icon"
              iconName={isCollapsed ? 'caret-right-filled' : 'caret-down-filled'}
              ariaLabel={
                isCollapsed
                  ? `Expand ${label}, ${descendants} nested`
                  : `Collapse ${label}`
              }
              ariaExpanded={!isCollapsed}
              onClick={() => onToggle(node.key)}
            />
          ) : null}
        </span>

        {/* The row's kind, first thing on the row, so a mixed tree reads at a glance. */}
        <span className="eh-hierarchy__kind">
          <Badge color={isEvent ? 'blue' : 'grey'}>{isEvent ? 'Event' : 'Campaign'}</Badge>
        </span>

        {control}

        <AddChildMenu
          under={label}
          canAddEvent={!!events.picker}
          onAdd={(kind) => onAddChild(node.key, kind)}
        />

        {isEvent ? (
          /* Removing an event row takes the event out of this tree (its node is deleted; the
             event is untouched). Blocked while it has saved children: the service refuses to
             delete a node with children. Unsaved ones go with it, like any branch. */
          <Button
            variant="inline-icon"
            iconName="remove"
            ariaLabel={
              descendants > 0
                ? `Remove ${node.eventId ? `event ${eventLabel}` : label} and its ${descendants} nested rows`
                : `Remove ${node.eventId ? `event ${eventLabel}` : label}`
            }
            disabled={hasSavedChildren(node)}
            disabledReason="This event has saved rows beneath it. Remove those first."
            onClick={() => onRemove(node.key)}
          />
        ) : (
          <Button
            variant="inline-icon"
            iconName="remove"
            /* Says what goes with it. Removing a branch takes its descendants, so the count
               is in the label rather than something the user discovers afterwards. */
            ariaLabel={
              descendants > 0
                ? `Remove ${label} and its ${descendants} nested campaigns`
                : `Remove ${label}`
            }
            onClick={() => onRemove(node.key)}
          />
        )}

        {duplicate ? (
          <Box color="text-status-error" fontSize="body-s">
            Already used in this tree
          </Box>
        ) : null}
      </div>

      {node.children.length > 0 && !isCollapsed ? (
        <ul className="eh-hierarchy__children">
          {node.children.map((child, childIndex) => (
            <Row
              key={child.key}
              node={child}
              parentLabel={label}
              index={childIndex}
              siblingCount={node.children.length}
              duplicates={duplicates}
              events={events}
              collapsed={collapsed}
              onToggle={onToggle}
              onEdit={onEdit}
              onChooseEvent={onChooseEvent}
              onAddChild={onAddChild}
              onRemove={onRemove}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export interface CampaignHierarchyProps {
  /** The event's name, shown on the root row so the tree is visibly anchored to it. */
  eventName: string;
  /**
   * The event's own SFDC Campaign ID, as currently typed in the form: the root row reads
   * "CampaignId (Event Name)", since that id is the parent of every campaign beneath it.
   */
  campaignId?: string;
  /** The event's descendants, top level first. Does not include the event. */
  value: readonly HierarchyNode[];
  onChange: (next: HierarchyNode[]) => void;
  /** This event's id, so the event list never offers the event itself. */
  eventId?: string;
  /**
   * The events an event row can choose from. Absent: "Event Hub event" is offered disabled.
   * `withNodes` is every event that already backs a hierarchy node (eventIdsWithNodes).
   */
  eventPicker?: EventPickerSource;
}

export default function CampaignHierarchy({
  eventName,
  campaignId,
  value,
  onChange,
  eventId = '',
  eventPicker,
}: CampaignHierarchyProps) {
  /* Which branches are folded, held here and not in the form's value: it is a view
     preference, and folding a branch shut is not an edit to the event. Tracking the folded
     ones rather than the open ones means a newly added node is expanded by default, which
     is what you want after clicking "Add child". */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const duplicates = duplicateCampaignIds(value);

  const ready = eventPicker?.status === 'ready' ? eventPicker : undefined;
  const labels = new Map<string, string>();
  const campaignIdOf = new Map<string, string>();
  for (const e of ready?.events ?? []) {
    if (!e.eventId) continue;
    const name = e.eventName?.trim() || e.eventId;
    const cid = e.sfdcCampaignId?.trim() ?? '';
    labels.set(e.eventId, cid ? `${name} - ${cid}` : name);
    campaignIdOf.set(e.eventId, cid);
  }
  const events: EventChoices = {
    picker: eventPicker,
    options: ready ? eventOptions(ready.events, eventId, eventIdsInTree(value), ready.withNodes) : [],
    labels,
  };

  function toggle(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function onAddChild(parentKey: string, kind: NodeKind) {
    const { nodes, childKey } = addChild(value, parentKey, kind);
    // A new row is no use hidden inside a branch that happened to be folded shut.
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(parentKey);
      next.delete(childKey);
      return next;
    });
    onChange(nodes);
  }

  const root = rootLabel(campaignId, eventName);

  return (
    <SpaceBetween size="s">
      {duplicates.size > 0 ? (
        <Alert type="warning" header="The same campaign id appears more than once">
          {[...duplicates].join(', ')} — a campaign id identifies one campaign, so it can
          only sit in one place in the hierarchy.
        </Alert>
      ) : null}

      <ul className="eh-hierarchy" aria-label="Campaign hierarchy">
        <li className="eh-hierarchy__item">
          {/* The event's own row: what the hierarchy hangs from, and not editable here. */}
          <div className="eh-hierarchy__row eh-hierarchy__row--root">
            <span className="eh-hierarchy__toggle" />
            <Box variant="strong">{root}</Box>
            <AddChildMenu
              under="this event"
              canAddEvent={!!eventPicker}
              onAdd={(kind) => onChange([...value, newNodeOfKind(kind)])}
            />
          </div>

          {value.length > 0 ? (
            <ul className="eh-hierarchy__children">
              {value.map((node, index) => (
                <Row
                  key={node.key}
                  node={node}
                  parentLabel={root}
                  index={index}
                  siblingCount={value.length}
                  duplicates={duplicates}
                  events={events}
                  collapsed={collapsed}
                  onToggle={toggle}
                  onEdit={(key, id) => onChange(setCampaignId(value, key, id))}
                  onChooseEvent={(key, id) =>
                    onChange(setEvent(value, key, id, campaignIdOf.get(id) ?? ''))
                  }
                  onAddChild={onAddChild}
                  onRemove={(key) => onChange(removeNode(value, key))}
                />
              ))}
            </ul>
          ) : null}
        </li>
      </ul>

      {value.length === 0 ? (
        <Box color="text-status-inactive" fontSize="body-s">
          Nothing under this event yet. Use Add child to add an SFDC Campaign ID, or another Event
          Hub event.
        </Box>
      ) : null}
    </SpaceBetween>
  );
}
